import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormControlLabel,
  FormGroup,
  Switch,
  Select,
  MenuItem,
  InputLabel,
  Button,
  Alert,
  CircularProgress,
  Divider,
  TextField,
} from '@mui/material';
import {
  Palette as ThemeIcon,
  Notifications as NotificationIcon,
  Language as LanguageIcon,
  Security as SecurityIcon,
  Email as EmailIcon,
  Computer as DesktopIcon,
  Save as SaveIcon,
  Undo as RevertIcon,
  Accessibility as AccessibilityIcon,
  LockReset as LockResetIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { auth as firebaseAuth } from '../firebase';

// Roles that receive the daily digest by default when the user hasn't set a preference.
const DIGEST_DEFAULT_ROLES = ['company_admin', 'admin', 'manager'];

const DEFAULT_SETTINGS = {
  theme: 'light',
  notifications: {
    email: true,
    browser: true,
    desktop: true,
    detectionAlerts: true,
    caseUpdates: true,
    systemMaintenance: true,
    weeklyReports: false,
    dailyDigest: false,
  },
  display: {
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12h',
  },
  accessibility: {
    highContrast: false,
    largeText: false,
    reducedMotion: false,
    screenReader: false,
  },
};

/** Merge the user's saved preferences over the defaults. */
const settingsFromUser = (user) => {
  const prefs = user?.preferences || {};
  const notifications = { ...DEFAULT_SETTINGS.notifications, ...(prefs.notifications || {}) };
  // Managers/admins get the digest by default when the user hasn't chosen yet.
  if (prefs.notifications?.dailyDigest === undefined) {
    notifications.dailyDigest = DIGEST_DEFAULT_ROLES.includes(user?.role);
  }
  return {
    theme: prefs.theme || DEFAULT_SETTINGS.theme,
    notifications,
    display: { ...DEFAULT_SETTINGS.display, ...(prefs.display || {}) },
    accessibility: { ...DEFAULT_SETTINGS.accessibility, ...(prefs.accessibility || {}) },
  };
};

const UserSettings = () => {
  const { user, updateProfile, changePassword } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  // Change-password form
  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' });
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(null);

  useEffect(() => {
    if (user) setSettings(settingsFromUser(user));
  }, [user]);

  const hasPasswordProvider = useMemo(() => {
    const providers = firebaseAuth.currentUser?.providerData || [];
    return providers.length === 0 || providers.some(p => p.providerId === 'password');
  }, []);

  const dirty = useMemo(
    () => JSON.stringify(settings) !== JSON.stringify(settingsFromUser(user)),
    [settings, user]
  );

  const handleSettingChange = (category, field, value) => {
    if (category === '') {
      setSettings(prev => ({ ...prev, [field]: value }));
    } else {
      setSettings(prev => ({ ...prev, [category]: { ...prev[category], [field]: value } }));
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      // One PUT /auth/profile; the backend merges the preferences subtree and
      // AuthContext updates `user`, which the Theme/Accessibility providers follow.
      await updateProfile({ preferences: settings });
      setSuccess('Settings saved successfully!');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleRevert = () => {
    setSettings(settingsFromUser(user));
    setError(null);
    setSuccess(null);
  };

  const passwordValidation = (() => {
    if (!passwordForm.next) return null;
    if (passwordForm.next.length < 8) return 'New password must be at least 8 characters';
    if (passwordForm.confirm && passwordForm.next !== passwordForm.confirm) return 'Passwords do not match';
    return null;
  })();

  const handleChangePassword = async () => {
    setPasswordError(null);
    setPasswordSuccess(null);
    if (!passwordForm.current || !passwordForm.next || !passwordForm.confirm) {
      setPasswordError('Fill in your current password, the new password and the confirmation');
      return;
    }
    if (passwordValidation) {
      setPasswordError(passwordValidation);
      return;
    }
    try {
      setPasswordBusy(true);
      const result = await changePassword(passwordForm.current, passwordForm.next);
      setPasswordSuccess(result?.message || 'Password changed successfully');
      setPasswordForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      setPasswordError(err.message || 'Password change failed');
    } finally {
      setPasswordBusy(false);
    }
  };

  if (!user) {
    return (
      <Container maxWidth="md">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          User Settings
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Customize your personal preferences and notification settings.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>{success}</Alert>
        )}

        <Grid container spacing={3}>
          {/* Appearance */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Appearance" avatar={<ThemeIcon />} />
              <CardContent>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Theme</InputLabel>
                  <Select
                    value={settings.theme}
                    label="Theme"
                    onChange={(e) => handleSettingChange('', 'theme', e.target.value)}
                  >
                    <MenuItem value="light">Light</MenuItem>
                    <MenuItem value="dark">Dark</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="body2" color="text.secondary">
                  Choose your preferred color theme for the application interface.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Notifications */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Notifications" avatar={<NotificationIcon />} />
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>Delivery Methods</Typography>
                <FormGroup sx={{ mb: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.notifications.email}
                        onChange={(e) => handleSettingChange('notifications', 'email', e.target.checked)}
                      />
                    }
                    label={
                      <Box display="flex" alignItems="center">
                        <EmailIcon sx={{ mr: 1, fontSize: 20 }} />
                        Email Notifications
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.notifications.browser}
                        onChange={(e) => handleSettingChange('notifications', 'browser', e.target.checked)}
                      />
                    }
                    label={
                      <Box display="flex" alignItems="center">
                        <DesktopIcon sx={{ mr: 1, fontSize: 20 }} />
                        Browser Notifications
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.notifications.desktop}
                        onChange={(e) => handleSettingChange('notifications', 'desktop', e.target.checked)}
                      />
                    }
                    label={
                      <Box display="flex" alignItems="center">
                        <DesktopIcon sx={{ mr: 1, fontSize: 20 }} />
                        Desktop Notifications
                      </Box>
                    }
                  />
                </FormGroup>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" gutterBottom>Notification Types</Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.notifications.detectionAlerts}
                        onChange={(e) => handleSettingChange('notifications', 'detectionAlerts', e.target.checked)}
                      />
                    }
                    label="New Detection Alerts"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.notifications.caseUpdates}
                        onChange={(e) => handleSettingChange('notifications', 'caseUpdates', e.target.checked)}
                      />
                    }
                    label="Case Status Updates"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.notifications.systemMaintenance}
                        onChange={(e) => handleSettingChange('notifications', 'systemMaintenance', e.target.checked)}
                      />
                    }
                    label="System Maintenance Alerts"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.notifications.weeklyReports}
                        onChange={(e) => handleSettingChange('notifications', 'weeklyReports', e.target.checked)}
                      />
                    }
                    label="Weekly Summary Reports"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.notifications.dailyDigest}
                        onChange={(e) => handleSettingChange('notifications', 'dailyDigest', e.target.checked)}
                      />
                    }
                    label="Daily Email Digest"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 6, mt: -1, mb: 1 }}>
                    A morning summary of new detections, open cases, SLA breaches and highest-risk assets.
                  </Typography>
                </FormGroup>
              </CardContent>
            </Card>
          </Grid>

          {/* Display */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Display Settings" avatar={<LanguageIcon />} />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Date Format</InputLabel>
                      <Select
                        value={settings.display.dateFormat}
                        label="Date Format"
                        onChange={(e) => handleSettingChange('display', 'dateFormat', e.target.value)}
                      >
                        <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
                        <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
                        <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Time Format</InputLabel>
                      <Select
                        value={settings.display.timeFormat}
                        label="Time Format"
                        onChange={(e) => handleSettingChange('display', 'timeFormat', e.target.value)}
                      >
                        <MenuItem value="12h">12 Hour</MenuItem>
                        <MenuItem value="24h">24 Hour</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Accessibility */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Accessibility" avatar={<AccessibilityIcon />} />
              <CardContent>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.accessibility.highContrast}
                        onChange={(e) => handleSettingChange('accessibility', 'highContrast', e.target.checked)}
                      />
                    }
                    label="High Contrast Mode"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.accessibility.largeText}
                        onChange={(e) => handleSettingChange('accessibility', 'largeText', e.target.checked)}
                      />
                    }
                    label="Large Text"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.accessibility.reducedMotion}
                        onChange={(e) => handleSettingChange('accessibility', 'reducedMotion', e.target.checked)}
                      />
                    }
                    label="Reduce Motion and Animations"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.accessibility.screenReader}
                        onChange={(e) => handleSettingChange('accessibility', 'screenReader', e.target.checked)}
                      />
                    }
                    label="Screen Reader Optimizations"
                  />
                </FormGroup>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  These settings help improve the experience for users with accessibility needs.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Preference actions */}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                startIcon={<RevertIcon />}
                onClick={handleRevert}
                disabled={loading || !dirty}
              >
                Revert changes
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={loading || !dirty}
              >
                {loading ? <CircularProgress size={20} /> : 'Save Settings'}
              </Button>
            </Box>
          </Grid>

          {/* Security */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Security" avatar={<SecurityIcon />} subheader="Change the password you use to sign in" />
              <CardContent>
                {!hasPasswordProvider && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Your account signs in with Google or Microsoft and may not have a password. Changing the
                    password requires re-authenticating with your current password; if you do not have one, use
                    the "Forgot password" link on the sign-in page to set one.
                  </Alert>
                )}
                {passwordError && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPasswordError(null)}>{passwordError}</Alert>
                )}
                {passwordSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPasswordSuccess(null)}>{passwordSuccess}</Alert>
                )}
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      type="password"
                      label="Current Password"
                      autoComplete="current-password"
                      value={passwordForm.current}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, current: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="password"
                      label="New Password"
                      autoComplete="new-password"
                      value={passwordForm.next}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, next: e.target.value }))}
                      error={Boolean(passwordValidation) && passwordForm.next.length < 8}
                      helperText="At least 8 characters"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="password"
                      label="Confirm New Password"
                      autoComplete="new-password"
                      value={passwordForm.confirm}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm: e.target.value }))}
                      error={Boolean(passwordForm.confirm) && passwordForm.next !== passwordForm.confirm}
                      helperText={passwordForm.confirm && passwordForm.next !== passwordForm.confirm ? 'Passwords do not match' : ' '}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box display="flex" justifyContent="flex-end">
                      <Button
                        variant="contained"
                        startIcon={passwordBusy ? <CircularProgress size={18} color="inherit" /> : <LockResetIcon />}
                        onClick={handleChangePassword}
                        disabled={passwordBusy || !passwordForm.current || !passwordForm.next || !passwordForm.confirm || Boolean(passwordValidation)}
                      >
                        Change Password
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default UserSettings;
